import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  PhoneIcon, 
  EnvelopeIcon, 
  ClockIcon,
  ChatBubbleLeftRightIcon,
  TruckIcon,
  TagIcon
} from '@heroicons/react/24/outline';

const ContactPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    budget: '',
    vehicleInfo: '',
    promoCode: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Pre-fill vehicle info from URL params
  useEffect(() => {
    const vehicleId = searchParams.get('vehicle');
    const vehicleName = searchParams.get('name');
    const stockNumber = searchParams.get('stock');
    
    if (vehicleId || vehicleName || stockNumber) {
      let vehicleInfo = '';
      
      if (vehicleName) {
        vehicleInfo = decodeURIComponent(vehicleName);
      } else if (vehicleId) {
        vehicleInfo = `#${vehicleId}`;
      }
      
      // Add stock number if provided
      if (stockNumber) {
        vehicleInfo += vehicleInfo ? ` (Stock: ${stockNumber})` : `Stock: ${stockNumber}`;
      }
      
      setFormData(prev => ({
        ...prev,
        vehicleInfo
      }));
    }
  }, [searchParams]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const response = await fetch('/api/v1/contact/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to submit form');
      }
      
      setSubmitted(true);
      setFormData({
        name: '',
        phone: '',
        budget: '',
        vehicleInfo: '',
        promoCode: '',
        message: ''
      });
    } catch (error) {
      console.error('Contact form error:', error);
      alert('Възникна грешка при изпращането. Моля, опитайте отново или се обадете директно.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-blue-50 py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-10 text-center border border-gray-100">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-10 h-10 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Благодарим Ви!</h2>
            <p className="text-lg text-gray-600 mb-8">
              Вашето съобщение беше изпратено успешно. Ще се свържем с вас в рамките на 24 часа.
            </p>
            <button
              onClick={() => setSubmitted(false)}
              className="bg-primary-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-primary-700 transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              Изпрати ново съобщение
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-white to-blue-50 py-8 lg:py-12">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Mobile: Compact Header with Quick Contact */}
        <div className="lg:hidden mb-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-3 text-center">Свържете се с нас</h1>
          
          {/* Quick Contact Bar - Mobile */}
          <div className="flex justify-center gap-3 mb-4">
            <a 
              href="tel:+359884141494" 
              className="flex-1 flex items-center justify-center gap-2 bg-primary-600 text-white py-3 px-4 rounded-xl font-semibold shadow-lg hover:bg-primary-700 transition-all"
            >
              <PhoneIcon className="w-5 h-5" />
              Обади се
            </a>
            <a 
              href="mailto:carcube.bg@gmail.com" 
              className="flex-1 flex items-center justify-center gap-2 bg-white text-primary-600 py-3 px-4 rounded-xl font-semibold shadow-lg border-2 border-primary-600 hover:bg-primary-50 transition-all"
            >
              <EnvelopeIcon className="w-5 h-5" />
              Имейл
            </a>
          </div>
        </div>

        {/* Desktop: Full Header */}
        <div className="hidden lg:block text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-primary-100 rounded-full mb-4">
            <ChatBubbleLeftRightIcon className="w-8 h-8 text-primary-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Свържете се с нас</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Имате въпроси? Ние сме тук да помогнем! Свържете се с нас за консултация относно вашия следващ автомобил.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
          {/* Contact Form - FIRST on Mobile, Right on Desktop */}
          <div className="lg:col-span-3 order-1 lg:order-2">
            <div className="bg-white rounded-2xl shadow-xl p-6 lg:p-8 border border-gray-100">
              <h2 className="text-xl lg:text-2xl font-bold text-gray-900 mb-4 lg:mb-6">Изпратете запитване</h2>
              
              <form onSubmit={handleSubmit} className="space-y-4 lg:space-y-6">
                {/* Name & Phone - Side by side on mobile */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Вашето име *
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="Иван Иванов"
                    />
                  </div>

                  <div>
                    <label htmlFor="phone" className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Телефон *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="+359 88 123 4567"
                    />
                  </div>
                </div>

                {/* Budget & Vehicle - Side by side */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="budget" className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Бюджет
                    </label>
                    <select
                      id="budget"
                      name="budget"
                      value={formData.budget}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all bg-white"
                    >
                      <option value="">Изберете бюджет</option>
                      <option value="0-20000">До 20,000 лв</option>
                      <option value="20000-40000">20,000 - 40,000 лв</option>
                      <option value="40000-60000">40,000 - 60,000 лв</option>
                      <option value="60000+">над 60,000 лв</option>
                    </select>
                  </div>

                  <div>
                    <label htmlFor="vehicleInfo" className="block text-sm font-semibold text-gray-700 mb-1.5">
                      Интересуващ автомобил
                    </label>
                    <input
                      type="text"
                      id="vehicleInfo"
                      name="vehicleInfo"
                      value={formData.vehicleInfo}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all"
                      placeholder="BMW X5, Mercedes GLE..."
                    />
                  </div>
                </div>

                {/* Promo Code */}
                <div>
                  <label htmlFor="promoCode" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    <TagIcon className="w-4 h-4 inline mr-1" />
                    Промо код (ако имате)
                  </label>
                  <input
                    type="text"
                    id="promoCode"
                    name="promoCode"
                    value={formData.promoCode}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all uppercase"
                    placeholder="PROMO2024"
                  />
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-semibold text-gray-700 mb-1.5">
                    Допълнителна информация
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    rows={3}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all resize-none"
                    placeholder="Какъв автомобил търсите, въпроси..."
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-4 rounded-xl font-bold text-lg hover:from-primary-700 hover:to-primary-800 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Изпращане...
                    </span>
                  ) : (
                    'Изпрати запитване'
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  С изпращането на формата се съгласявате с нашата{' '}
                  <a href="/privacy-policy" className="text-primary-600 hover:underline">Политика за поверителност</a>
                </p>
              </form>
            </div>
          </div>

          {/* Contact Information - SECOND on Mobile (after form), Left on Desktop */}
          <div className="lg:col-span-2 order-2 lg:order-1 space-y-4 lg:space-y-6">
            {/* Mobile: Compact Contact Info */}
            <div className="lg:hidden">
              <div className="bg-white rounded-2xl shadow-lg p-4 border border-gray-100">
                <div className="grid grid-cols-2 gap-4">
                  {/* Phone */}
                  <div className="text-center">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <PhoneIcon className="h-5 w-5 text-primary-600" />
                    </div>
                    <p className="text-xs text-gray-500">Телефон</p>
                    <a href="tel:+359884141494" className="text-sm font-bold text-primary-600">
                      +359 88 414 1494
                    </a>
                  </div>
                  
                  {/* Email */}
                  <div className="text-center">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-2">
                      <EnvelopeIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <p className="text-xs text-gray-500">Имейл</p>
                    <a href="mailto:carcube.bg@gmail.com" className="text-sm font-bold text-blue-600">
                      carcube.bg@gmail.com
                    </a>
                  </div>
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-100 text-center">
                  <div className="flex items-center justify-center gap-2 text-sm">
                    <ClockIcon className="h-4 w-4 text-green-600" />
                    <span className="text-gray-600">Пон-Нед: 9:00-18:00</span>
                    <span className="text-red-500 font-medium">(Без почивен)</span>
                  </div>
                </div>
              </div>
              
              {/* Why Choose Us - Mobile Compact */}
              <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl shadow-lg p-4 text-white mt-4">
                <h3 className="text-sm font-semibold mb-3 text-center">Защо да изберете нас?</h3>
                <div className="grid grid-cols-3 gap-2 text-xs">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-primary-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    10+ години опит
                  </div>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-primary-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Безплатна консултация
                  </div>
                  <div className="flex items-center">
                    <svg className="w-4 h-4 mr-2 text-primary-200 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Гарантирана сигурност
                  </div>
                </div>
              </div>
            </div>

            {/* Desktop: Full Contact Cards */}
            <div className="hidden lg:block space-y-6">
              {/* Phone Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
                    <PhoneIcon className="h-6 w-6 text-primary-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-4">Телефон</h3>
                </div>
                <a href="tel:+359884141494" className="text-2xl font-bold text-primary-600 hover:text-primary-700 transition-colors">
                  +359 88 414 1494
                </a>
                <p className="text-sm text-gray-500 mt-2">Пон-Нед 9:00 - 18:00</p>
              </div>

              {/* Email Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
                    <EnvelopeIcon className="h-6 w-6 text-blue-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-4">Имейл</h3>
                </div>
                <a href="mailto:carcube.bg@gmail.com" className="text-lg font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                  carcube.bg@gmail.com
                </a>
                <p className="text-sm text-gray-500 mt-2">Отговаряме в рамките на 24 часа</p>
              </div>

              {/* Working Hours Card */}
              <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100 hover:shadow-xl transition-shadow">
                <div className="flex items-center mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                    <ClockIcon className="h-6 w-6 text-green-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 ml-4">Работно време</h3>
                </div>
                <div className="space-y-1 text-gray-600">
                  <p>Понеделник - Неделя: <span className="font-medium">9:00 - 18:00</span></p>
                  <p><span className="font-medium text-red-500">Без почивен ден</span></p>
                </div>
              </div>

              {/* Why Contact Us */}
              <div className="bg-gradient-to-br from-primary-600 to-primary-700 rounded-2xl shadow-lg p-6 text-white">
                <h3 className="text-lg font-semibold mb-4">Защо да изберете нас?</h3>
                <ul className="space-y-3">
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-3 text-primary-200" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Над 10 години опит
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-3 text-primary-200" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Безплатна консултация
                  </li>
                  <li className="flex items-center">
                    <svg className="w-5 h-5 mr-3 text-primary-200" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Гарантирана сигурност
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
